import { Chart as ChartJS, Plugin } from 'chart.js';

/**
 * ТЗ 0119 — графики chart.js в светлой и тёмной теме.
 *
 * Цвета ERP — CSS-переменные (`var(--c-…)`, `var(--k-…)`), а canvas их не
 * понимает: `ctx.fillStyle = 'var(--x)'` молча игнорируется. Поэтому:
 *
 * 1. плагин перед каждым `update` разворачивает `var()` в цветах наборов
 *    данных и опций в готовый цвет темы (исходную строку помнит — при смене
 *    темы разворачивает заново, а не из уже развёрнутой);
 * 2. страховка: `fillStyle`/`strokeStyle` canvas разворачивают `var()` сами —
 *    на случай цвета, пришедшего мимо опций (свой плагин страницы рисует
 *    напрямую). Других значений перехват не касается;
 * 3. смена `data-theme` на <html> перерисовывает все графики.
 */

const VAR_RE = /var\((--[\w-]+)\)/g;
const cache = new Map<string, string>();

/** '#rgb' | '#rrggbb' | '#rrggbbaa' | 'rgb(a)(…)' → [r, g, b, a] или null. */
const parseRgba = (value: string): number[] | null => {
  const v = value.trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(v);
  if (hex) {
    const h =
      hex[1].length === 3
        ? hex[1]
            .split('')
            .map((ch) => ch + ch)
            .join('')
        : hex[1];
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
      h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1,
    ];
  }
  const rgb = /^rgba?\(([^)]+)\)$/i.exec(v);
  if (!rgb) return null;
  const parts = rgb[1]
    .split(/[\s,/]+/)
    .filter(Boolean)
    .map(Number);
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
  return [parts[0], parts[1], parts[2], parts[3] ?? 1];
};

/**
 * `color-mix(in srgb, <цвет> P%, transparent)` (так chartFaded и withAlpha
 * бледнят цвет темы) → `rgba(…)`. Canvas color-mix понимает, а @kurkle/color,
 * из которого chart.js выводит цвет наведения, — нет: наведение рисовалось
 * чужим цветом.
 */
const MIX_RE = /^color-mix\(in srgb,\s*(.+?)\s+([\d.]+)%,\s*transparent\)$/i;
const flattenMix = (value: string): string => {
  const m = MIX_RE.exec(value.trim());
  const rgba = m && parseRgba(m[1]);
  if (!m || !rgba) return value;
  const a = (rgba[3] * Number(m[2])) / 100;
  return `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${
    Math.round(a * 1000) / 1000
  })`;
};

/** `var(--x)` (в том числе внутри `color-mix(…)`) → значение текущей темы. */
export const resolveCssColor = (value: string): string => {
  if (!value.includes('var(')) return value;
  const hit = cache.get(value);
  if (hit) return hit;
  const style = getComputedStyle(document.documentElement);
  let out = value;
  // значение переменной может само ссылаться на переменную — до 3 уровней
  for (let i = 0; i < 3 && out.includes('var('); i += 1) {
    out = out.replace(
      VAR_RE,
      (_, name: string) =>
        style.getPropertyValue(name).trim() || 'rgba(0, 0, 0, 0)',
    );
  }
  out = flattenMix(out);
  cache.set(value, out);
  return out;
};

interface IResolved {
  original: string;
  resolved: string;
}

/** Объект → ключ → исходная строка и то, во что мы её развернули. */
const memory = new WeakMap<object, Map<string | number, IResolved>>();

const isPlain = (value: unknown): value is Record<string, unknown> =>
  !!value &&
  typeof value === 'object' &&
  (Array.isArray(value) || Object.getPrototypeOf(value) === Object.prototype);

const resolveIn = (target: Record<string | number, unknown>, depth: number) => {
  if (depth > 6) return;
  let mem = memory.get(target);
  Object.keys(target).forEach((key) => {
    // служебные поля и `data` набора (числа/точки) — не цвета
    if (key.startsWith('_') || key === 'data') return;
    const value = target[key];
    if (typeof value === 'string') {
      const prev = mem?.get(key);
      if (prev && prev.resolved === value) {
        // наш же развёрнутый цвет — развернуть заново (тема могла смениться)
        const next = resolveCssColor(prev.original);
        if (next !== value) target[key] = next;
        prev.resolved = next;
        return;
      }
      if (value.includes('var(')) {
        const next = resolveCssColor(value);
        if (!mem) {
          mem = new Map();
          memory.set(target, mem);
        }
        mem.set(key, { original: value, resolved: next });
        target[key] = next;
      }
      return;
    }
    if (isPlain(value)) resolveIn(value as Record<string, unknown>, depth + 1);
  });
};

const themePlugin: Plugin = {
  id: 'erpTheme',
  beforeUpdate(chart) {
    const { config } = chart;
    if (config.options) {
      resolveIn((config.options as unknown) as Record<string, unknown>, 1);
    }
    (config.data?.datasets || []).forEach((dataset) =>
      resolveIn((dataset as unknown) as Record<string, unknown>, 1),
    );
  },
};

const patchCanvas = () => {
  const proto = CanvasRenderingContext2D.prototype;
  (['fillStyle', 'strokeStyle', 'shadowColor'] as const).forEach((prop) => {
    const desc = Object.getOwnPropertyDescriptor(proto, prop);
    if (!desc?.set || !desc.get) return;
    const { set } = desc;
    Object.defineProperty(proto, prop, {
      ...desc,
      set(value: unknown) {
        set.call(
          this,
          typeof value === 'string' && value.includes('var(')
            ? resolveCssColor(value)
            : value,
        );
      },
    });
  });
};

/**
 * Умолчания chart.js — для графиков без своих цветов (подписи, сетка, кольцо
 * доли). Светлые значения — прежние умолчания chart.js (#666,
 * rgba(0,0,0,.1), #fff); готовым цветом, а не var(): из умолчаний chart.js
 * выводит и цвет наведения.
 */
const applyDefaults = () => {
  ChartJS.defaults.color = resolveCssColor('var(--k-fg-666666)');
  ChartJS.defaults.borderColor = resolveCssColor('var(--k-border-000000-a100)');
  ChartJS.defaults.backgroundColor = resolveCssColor('var(--k-bg-000000-a100)');
  ChartJS.defaults.elements.arc.borderColor = resolveCssColor(
    'var(--k-bg-ffffff)',
  );
};

const watchTheme = () => {
  const redraw = () => {
    cache.clear();
    applyDefaults();
    Object.values(ChartJS.instances).forEach((chart) => chart?.update('none'));
  };
  new MutationObserver(redraw).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });
};

let installed = false;

/** Один раз на приложение (src/index.tsx): плагин, страховка canvas, слежение. */
export const installChartTheme = (): void => {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  ChartJS.register(themePlugin);
  patchCanvas();
  applyDefaults();
  watchTheme();
};
